import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import PortalFooter from '../../components/Layout/PortalFooter';
import portalAPI from '../../services/portalAPI';
import './PortalMemberDetail.css';
import '../../styles/portal-brand.css';

// Types matching the backend API response
interface MemberProfile {
  id: number;
  member_code: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  joined_at: string;
  loyalty_progress: number;
  bags_until_reward: number;
  available_rewards: number;
  total_purchases: number;
  last_purchase_date: string | null;
  linked_store_id: number | null;
  linked_store_name: string | null;
}

interface FeedBin {
  id: number;
  name: string;
  category: string;
  category_icon: string;
  product_name: string;
  form_type: string;
  form_type_display: string;
  current_lbs: number;
  capacity_lbs: number;
  percent_full: number;
  daily_consumption: number;
  days_remaining: number | null;
  run_out_date: string | null;
  status: 'critical' | 'low' | 'ok' | 'good' | 'empty' | 'unknown';
  alerts_enabled: boolean;
}

interface Animal {
  id: number;
  category: string;
  category_icon: string;
  animal_type: string;
  name: string;
  count: number;
  life_stage: string;
  feed_product_name: string;
  is_red_bridge_feed: boolean;
  daily_consumption_lbs: number;
}

interface PurchaseItem {
  product_name: string;
  quantity: number;
  price: number;
  is_red_bridge: boolean;
  bag_size_lbs: number;
  loyalty_bags: number;
}

interface Purchase {
  id: number;
  purchase_date: string;
  transaction_timestamp?: string | null;
  journal_number?: string | null;
  register_id?: string | null;
  cashier?: string | null;
  source?: string | null;
  included_free_bag?: boolean;
  is_voided?: boolean;
  void_reason?: string | null;
  voided_at?: string | null;
  store_name: string;
  is_this_store: boolean;
  total: number;
  red_bridge_bags: number;
  loyalty_bags_earned: number;
  verification_status: string;
  items: PurchaseItem[];
}

interface TypicalProduct {
  product_name: string;
  frequency: number;
  avg_quantity: number;
}

interface Analytics {
  avg_frequency_days: number | null;
  avg_order_size: number | null;
  typical_products: TypicalProduct[];
  predicted_next_visit: string | null;
  lifetime_value: number;
  confidence_score?: number;
}

interface OtherProduct {
  product_name: string;
  purchase_count: number;
  avg_frequency_days: number | null;
  days_since_last: number | null;
  last_purchase: string | null;
  predicted_restock: string | null;
}

interface MemberDetailData {
  member: MemberProfile;
  feed_bins: FeedBin[];
  animals: Animal[];
  recent_purchases: Purchase[];
  analytics: Analytics;
  other_products: OtherProduct[];
}

const getAnimalIcon = (category: string): string => {
  const icons: Record<string, string> = {
    'chickens': '🐔',
    'ducks': '🦆',
    'turkeys': '🦃',
    'geese': '🪿',
    'pigs': '🐷',
    'goats': '🐐',
    'sheep': '🐑',
    'cattle': '🐄',
    'horses': '🐴',
    'rabbits': '🐰',
    'dogs': '🐕',
    'cats': '🐈',
  };
  return icons[category.toLowerCase()] || '🐾';
};

const getBinStatusColor = (status: string): string => {
  switch (status) {
    case 'critical': return '#dc2626';
    case 'low': return '#f59e0b';
    case 'ok': return '#3b82f6';
    case 'good': return '#10b981';
    default: return '#6b7280';
  }
};

const getBinStatusLabel = (status: string): string => {
  switch (status) {
    case 'critical': return 'Critical - Restock Now';
    case 'low': return 'Low - Order Soon';
    case 'ok': return 'OK';
    case 'good': return 'Good';
    default: return status;
  }
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const PortalMemberDetail: React.FC = () => {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const { user } = usePortalAuth();

  const [data, setData] = useState<MemberDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPurchase, setExpandedPurchase] = useState<number | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Lead-level edit/void state
  const [editingPurchase, setEditingPurchase] = useState<Purchase | null>(null);
  const [voidingPurchase, setVoidingPurchase] = useState<Purchase | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [editForm, setEditForm] = useState({ red_bridge_bags: '', total: '', journal_number: '', included_free_bag: false, notes: '' });
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMemberDetail = async () => {
      if (!memberId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await portalAPI.get(`/portal/bridge-club/${memberId}/member_detail/`);
        setData(response.data);
      } catch (err: any) {
        console.error('Error fetching member detail:', err);
        if (err.response?.status === 404) {
          setError('Member not found');
        } else if (err.response?.status === 403) {
          setError('You do not have permission to view this member');
        } else {
          setError('Failed to load member details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMemberDetail();
  }, [memberId, refreshTick]);

  const openEditPurchase = (p: Purchase) => {
    setActionError(null);
    setEditForm({
      red_bridge_bags: String(p.red_bridge_bags ?? 0),
      total: String(p.total ?? 0),
      journal_number: p.journal_number || '',
      included_free_bag: !!p.included_free_bag,
      notes: '',
    });
    setEditingPurchase(p);
  };

  const submitEditPurchase = async () => {
    if (!editingPurchase) return;
    setActionBusy(true);
    setActionError(null);
    try {
      await portalAPI.patch(`/portal/bridge-club/purchases/${editingPurchase.id}/`, {
        red_bridge_bags: parseInt(editForm.red_bridge_bags) || 0,
        total: parseFloat(editForm.total) || 0,
        journal_number: editForm.journal_number || null,
        included_free_bag: editForm.included_free_bag,
        notes: editForm.notes,
      });
      setEditingPurchase(null);
      setRefreshTick((t) => t + 1);
    } catch (err: any) {
      setActionError(err.response?.data?.message || err.response?.data?.error || 'Failed to save changes');
    } finally {
      setActionBusy(false);
    }
  };

  const submitVoidPurchase = async () => {
    if (!voidingPurchase) return;
    setActionBusy(true);
    setActionError(null);
    try {
      await portalAPI.post(`/portal/bridge-club/purchases/${voidingPurchase.id}/void/`, {
        reason: voidReason,
      });
      setVoidingPurchase(null);
      setVoidReason('');
      setRefreshTick((t) => t + 1);
    } catch (err: any) {
      setActionError(err.response?.data?.message || err.response?.data?.error || 'Failed to void purchase');
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="portal-container member-detail-page">
        <div className="member-detail-loading">
          <div className="loading-spinner" />
          <p>Loading member details...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="portal-container member-detail-page">
        <div className="member-detail-error">
          <span className="error-icon">!</span>
          <h2>{error || 'Unable to load member details'}</h2>
          <button onClick={() => navigate(-1)} className="btn-rbf-secondary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Destructure with defaults to prevent .filter()/.map() errors on undefined
  const {
    member,
    feed_bins = [],
    animals = [],
    recent_purchases = [],
    analytics = { typical_products: [], avg_frequency_days: null, avg_order_size: null, predicted_next_visit: null, lifetime_value: 0 },
    other_products = []
  } = data;

  return (
    <div className="portal-container member-detail-page">
      <div className="member-detail-content">
        {/* Header Section */}
        <div className="member-detail-header">
          <button onClick={() => navigate(-1)} className="back-button">
            ← Back
          </button>

          <div className="member-header-info">
            <div className="member-avatar">
              {member.first_name.charAt(0)}{member.last_name.charAt(0)}
            </div>
            <div className="member-header-text">
              <h1>{member.first_name} {member.last_name}</h1>
              <div className="member-code">#{member.member_code}</div>
              <div className="member-contact">
                {member.email} • {member.phone || 'No phone'}
              </div>
              <div className="member-location">
                {member.city && member.state ? `${member.city}, ${member.state}` : 'Location not set'}
              </div>
            </div>
          </div>

          <div className="member-header-stats">
            {/* Loyalty Status */}
            <div className="loyalty-card">
              <div className="loyalty-title">Loyalty Progress</div>
              <div className="loyalty-progress">
                <div className="loyalty-dots">
                  {[...Array(10)].map((_, i) => (
                    <span
                      key={i}
                      className={`loyalty-dot ${i < member.loyalty_progress ? 'filled' : ''}`}
                    />
                  ))}
                </div>
                <div className="loyalty-text">
                  {member.loyalty_progress}/10 bags
                </div>
              </div>
              {member.available_rewards > 0 && (
                <div className="rewards-badge">
                  {member.available_rewards} FREE {member.available_rewards === 1 ? 'BAG' : 'BAGS'} AVAILABLE!
                </div>
              )}
              {member.bags_until_reward > 0 && member.available_rewards === 0 && (
                <div className="bags-until">
                  {member.bags_until_reward} more until reward
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="quick-stats">
              <div className="quick-stat">
                <span className="stat-value">{member.total_purchases}</span>
                <span className="stat-label">Total Purchases</span>
              </div>
              <div className="quick-stat">
                <span className="stat-value">{formatDate(member.last_purchase_date)}</span>
                <span className="stat-label">Last Visit</span>
              </div>
              <div className="quick-stat">
                <span className="stat-value">{formatDate(member.joined_at)}</span>
                <span className="stat-label">Member Since</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <button
              className="btn-rbf-primary"
              onClick={() => navigate(`/portal/bridge-club?tab=checkin&member=${member.member_code}`)}
            >
              Check In Customer
            </button>
          </div>
        </div>

        {/* Feed Bins Section - MOST IMPORTANT FOR RETAIL */}
        <section className="detail-section feed-bins-section">
          <h2 className="section-title">
            <span className="section-icon">🛢️</span>
            Feed Bin Status
            {feed_bins.some(b => b.status === 'critical' || b.status === 'low') && (
              <span className="section-alert">Action Needed</span>
            )}
          </h2>
          {feed_bins.length === 0 ? (
            <div className="empty-state">
              <p>No feed bins set up yet</p>
              <p className="empty-hint">Customer hasn't configured feed tracking</p>
            </div>
          ) : (
            <div className="feed-bins-grid">
              {feed_bins.map((bin) => (
                <div key={bin.id} className={`feed-bin-card status-${bin.status}`}>
                  <div className="bin-header">
                    <div className="bin-name-row">
                      <span className="bin-category-icon">{bin.category_icon}</span>
                      <span className="bin-name">{bin.name}</span>
                    </div>
                    <span
                      className="bin-status-badge"
                      style={{ backgroundColor: getBinStatusColor(bin.status) }}
                    >
                      {getBinStatusLabel(bin.status)}
                    </span>
                  </div>

                  <div className="bin-visual">
                    <div className="bin-container">
                      <div
                        className="bin-fill"
                        style={{
                          height: `${bin.percent_full}%`,
                          backgroundColor: getBinStatusColor(bin.status)
                        }}
                      />
                    </div>
                    <div className="bin-stats-vertical">
                      <div className="bin-percent">{Math.round(bin.percent_full)}%</div>
                      {bin.days_remaining !== null && (
                        <div className={`bin-days ${bin.days_remaining <= 3 ? 'critical' : bin.days_remaining <= 7 ? 'low' : ''}`}>
                          {bin.days_remaining} days left
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bin-details">
                    <div className="bin-detail-row">
                      <span className="label">Product:</span>
                      <span>{bin.product_name || 'Unknown'}</span>
                    </div>
                    {bin.form_type_display && (
                      <div className="bin-detail-row">
                        <span className="label">Form:</span>
                        <span>{bin.form_type_display}</span>
                      </div>
                    )}
                    <div className="bin-detail-row">
                      <span className="label">Current:</span>
                      <span>{Math.round(bin.current_lbs)} lbs</span>
                    </div>
                    {bin.daily_consumption > 0 && (
                      <div className="bin-detail-row">
                        <span className="label">Usage:</span>
                        <span>{bin.daily_consumption.toFixed(1)} lbs/day</span>
                      </div>
                    )}
                    {bin.run_out_date && (
                      <div className="bin-detail-row">
                        <span className="label">Empty by:</span>
                        <span className={bin.status === 'critical' ? 'text-danger' : ''}>
                          {formatDate(bin.run_out_date)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Restock Prompt - the key sales driver! */}
                  {(bin.status === 'critical' || bin.status === 'low') && (
                    <div className={`restock-prompt ${bin.status}`}>
                      <span className="restock-icon">{bin.status === 'critical' ? '🚨' : '💡'}</span>
                      <span className="restock-text">
                        {bin.status === 'critical'
                          ? `Suggest restocking ${bin.product_name || 'feed'} today!`
                          : `Good time to mention ${bin.product_name || 'feed'} restock`
                        }
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Animals Section */}
        {animals.length > 0 && (
          <section className="detail-section">
            <h2 className="section-title">
              <span className="section-icon">🐾</span>
              Animals
            </h2>
            <div className="animals-grid">
              {animals.map((animal) => (
                <div key={animal.id} className="animal-card">
                  <div className="animal-icon">{animal.category_icon || getAnimalIcon(animal.category)}</div>
                  <div className="animal-info">
                    <div className="animal-name">
                      {animal.name || animal.animal_type || animal.category}
                      {animal.is_red_bridge_feed && <span className="rb-badge">RB</span>}
                    </div>
                    <div className="animal-count">
                      {animal.count} {animal.count === 1 ? 'head' : 'head'}
                      {animal.life_stage && <span className="animal-stage"> • {animal.life_stage}</span>}
                    </div>
                    {animal.daily_consumption_lbs > 0 && (
                      <div className="animal-consumption">
                        ~{animal.daily_consumption_lbs.toFixed(1)} lbs/day
                      </div>
                    )}
                    {animal.feed_product_name && (
                      <div className="animal-feed">{animal.feed_product_name}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Purchase History Section */}
        <section className="detail-section">
          <h2 className="section-title">
            <span className="section-icon">🧾</span>
            Purchase History
          </h2>
          {recent_purchases.length === 0 ? (
            <div className="empty-state">
              <p>No purchases recorded yet</p>
            </div>
          ) : (
            <div className="purchases-list">
              {recent_purchases.map((purchase) => (
                <div
                  key={purchase.id}
                  className={`purchase-card ${purchase.is_this_store ? 'this-store' : ''} ${purchase.is_voided ? 'voided' : ''}`}
                  style={purchase.is_voided ? { opacity: 0.6 } : undefined}
                >
                  <div
                    className="purchase-header"
                    onClick={() => setExpandedPurchase(
                      expandedPurchase === purchase.id ? null : purchase.id
                    )}
                    style={purchase.is_voided ? { textDecoration: 'line-through' } : undefined}
                  >
                    <div className="purchase-date">
                      {formatDate(purchase.purchase_date)}
                      {purchase.is_voided && (
                        <span style={{ marginLeft: 6, background: '#dc2626', color: '#fff', fontSize: 10, padding: '1px 6px', borderRadius: 6, textDecoration: 'none', display: 'inline-block' }}>
                          VOIDED
                        </span>
                      )}
                    </div>
                    <div className="purchase-store">
                      {purchase.store_name}
                      {purchase.is_this_store && <span className="this-store-badge">Your Store</span>}
                    </div>
                    <div className="purchase-summary">
                      <span className="rb-bags">{purchase.red_bridge_bags} RB bags</span>
                      <span className="purchase-total">${purchase.total.toFixed(2)}</span>
                    </div>
                    <span className="expand-icon">
                      {expandedPurchase === purchase.id ? '▼' : '▶'}
                    </span>
                  </div>

                  {expandedPurchase === purchase.id && (
                    <div className="purchase-detail-meta" style={{ padding: '8px 12px', fontSize: 13, color: '#475569', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {purchase.journal_number && <span><strong>Journal:</strong> {purchase.journal_number}</span>}
                      {purchase.transaction_timestamp && (
                        <span><strong>Time:</strong> {new Date(purchase.transaction_timestamp).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                      )}
                      {purchase.source && <span><strong>Source:</strong> {purchase.source.replace('_', ' ')}</span>}
                      {purchase.cashier && <span><strong>Cashier:</strong> {purchase.cashier}</span>}
                      {purchase.register_id && <span><strong>Register:</strong> {purchase.register_id}</span>}
                      {purchase.included_free_bag && <span>🎁 Free bag redemption</span>}
                      {purchase.is_voided && purchase.void_reason && (
                        <span style={{ color: '#dc2626' }}><strong>Void reason:</strong> {purchase.void_reason}</span>
                      )}
                    </div>
                  )}

                  {expandedPurchase === purchase.id && !purchase.is_voided && (
                    <div className="purchase-actions" style={{ display: 'flex', gap: 8, padding: '0 12px 8px' }}>
                      <button
                        onClick={() => openEditPurchase(purchase)}
                        style={{ padding: '8px 16px', minHeight: 44, borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => { setActionError(null); setVoidReason(''); setVoidingPurchase(purchase); }}
                        style={{ padding: '8px 16px', minHeight: 44, borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}
                      >
                        Void
                      </button>
                    </div>
                  )}

                  {expandedPurchase === purchase.id && purchase.items?.length > 0 && (
                    <div className="purchase-items">
                      <table>
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Qty</th>
                            <th>Size</th>
                            <th>Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(purchase.items || []).map((item, idx) => (
                            <tr key={idx} className={item.is_red_bridge ? 'rb-item' : ''}>
                              <td>
                                {item.product_name}
                                {item.is_red_bridge && <span className="rb-tag">RB</span>}
                              </td>
                              <td>{item.quantity}</td>
                              <td>{item.bag_size_lbs ? `${item.bag_size_lbs} lbs` : '-'}</td>
                              <td>${item.price.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Analytics Section (Powered by Laura) */}
        <section className="detail-section">
          <h2 className="section-title">
            <span className="section-icon">🔮</span>
            Laura's Insights
            <span className="powered-by">AI-Powered</span>
          </h2>
          <div className="analytics-grid">
            {analytics.predicted_next_visit && (
              <div className="analytics-card highlight">
                <div className="analytics-value">
                  {formatDate(analytics.predicted_next_visit)}
                </div>
                <div className="analytics-label">Predicted Next Visit</div>
              </div>
            )}

            {analytics.avg_frequency_days !== null && (
              <div className="analytics-card">
                <div className="analytics-value">
                  {Math.round(analytics.avg_frequency_days)} days
                </div>
                <div className="analytics-label">Avg. Between Visits</div>
              </div>
            )}

            {analytics.avg_order_size !== null && (
              <div className="analytics-card">
                <div className="analytics-value">
                  {analytics.avg_order_size.toFixed(1)} bags
                </div>
                <div className="analytics-label">Avg. per Visit</div>
              </div>
            )}

            {analytics.lifetime_value > 0 && (
              <div className="analytics-card">
                <div className="analytics-value">
                  ${analytics.lifetime_value.toFixed(0)}
                </div>
                <div className="analytics-label">Lifetime Value</div>
              </div>
            )}

            {analytics.typical_products.length > 0 && (
              <div className="analytics-card wide">
                <div className="analytics-label">Preferred Products</div>
                <div className="preferred-products">
                  {analytics.typical_products.map((product, idx) => (
                    <span key={idx} className="product-chip">
                      {product.product_name}
                      <span className="product-freq">×{product.frequency}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Other Products Section */}
        {other_products.length > 0 && (
          <section className="detail-section">
            <h2 className="section-title">
              <span className="section-icon">📦</span>
              Other Products They Buy
              <span className="section-hint">Non-Red Bridge items from receipts</span>
            </h2>
            <div className="other-products-grid">
              {other_products.map((product, idx) => (
                <div key={idx} className="other-product-card">
                  <div className="product-name">{product.product_name}</div>
                  <div className="product-details">
                    <span>Bought {product.purchase_count}×</span>
                    {product.avg_frequency_days && (
                      <span>Every ~{Math.round(product.avg_frequency_days)}d</span>
                    )}
                  </div>
                  {product.days_since_last !== null && (
                    <div className="product-last">
                      Last: {product.days_since_last === 0 ? 'Today' : `${product.days_since_last} days ago`}
                    </div>
                  )}
                  {product.predicted_restock && (
                    <div className="product-restock">
                      May need ~{formatDate(product.predicted_restock)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Alert Banner for Critical Bins */}
        {feed_bins.some(bin => bin.status === 'critical' || bin.status === 'low') && (
          <div className="alert-banner">
            <span className="alert-icon">⚠️</span>
            <span className="alert-text">
              This customer has bins that need attention!
              {feed_bins.filter(b => b.status === 'critical').length > 0 && (
                <strong> {feed_bins.filter(b => b.status === 'critical').length} critical</strong>
              )}
              {feed_bins.filter(b => b.status === 'low').length > 0 && (
                <strong> {feed_bins.filter(b => b.status === 'low').length} low</strong>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Edit Purchase Modal */}
      {editingPurchase && (
        <div style={modalOverlayStyle} onClick={() => !actionBusy && setEditingPurchase(null)}>
          <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Edit Purchase</h3>
            {actionError && <div style={{ color: '#dc2626', marginBottom: 8 }}>{actionError}</div>}
            <label style={modalLabelStyle}>Red Bridge Bags</label>
            <input type="number" min="0" value={editForm.red_bridge_bags}
              onChange={(e) => setEditForm({ ...editForm, red_bridge_bags: e.target.value })} style={modalInputStyle} />
            <label style={modalLabelStyle}>Total ($)</label>
            <input type="number" min="0" step="0.01" value={editForm.total}
              onChange={(e) => setEditForm({ ...editForm, total: e.target.value })} style={modalInputStyle} />
            <label style={modalLabelStyle}>Journal Number</label>
            <input type="text" value={editForm.journal_number}
              onChange={(e) => setEditForm({ ...editForm, journal_number: e.target.value })} style={modalInputStyle} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0', minHeight: 44 }}>
              <input type="checkbox" checked={editForm.included_free_bag}
                onChange={(e) => setEditForm({ ...editForm, included_free_bag: e.target.checked })} style={{ width: 20, height: 20 }} />
              Included a free bag redemption
            </label>
            <label style={modalLabelStyle}>Notes</label>
            <input type="text" value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} style={modalInputStyle} />
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={() => setEditingPurchase(null)} disabled={actionBusy} style={modalCancelBtnStyle}>Cancel</button>
              <button onClick={submitEditPurchase} disabled={actionBusy} style={modalPrimaryBtnStyle}>
                {actionBusy ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Void Purchase Modal */}
      {voidingPurchase && (
        <div style={modalOverlayStyle} onClick={() => !actionBusy && setVoidingPurchase(null)}>
          <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Void Purchase</h3>
            <p style={{ color: '#475569', fontSize: 14 }}>
              This removes the entry from the member's loyalty count and recalculates their punch card.
            </p>
            {actionError && <div style={{ color: '#dc2626', marginBottom: 8 }}>{actionError}</div>}
            <label style={modalLabelStyle}>Reason for voiding this entry?</label>
            <input type="text" value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. duplicate of earlier entry" style={modalInputStyle} autoFocus />
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={() => setVoidingPurchase(null)} disabled={actionBusy} style={modalCancelBtnStyle}>Cancel</button>
              <button onClick={submitVoidPurchase} disabled={actionBusy}
                style={{ ...modalPrimaryBtnStyle, background: '#dc2626' }}>
                {actionBusy ? 'Voiding…' : 'Void Entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      <PortalFooter />
    </div>
  );
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
};
const modalCardStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 420,
  maxHeight: '90vh', overflowY: 'auto',
};
const modalLabelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '8px 0 4px',
};
const modalInputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 16,
};
const modalCancelBtnStyle: React.CSSProperties = {
  flex: 1, padding: '10px 16px', minHeight: 44, borderRadius: 8, border: '1px solid #cbd5e1',
  background: '#fff', cursor: 'pointer',
};
const modalPrimaryBtnStyle: React.CSSProperties = {
  flex: 1, padding: '10px 16px', minHeight: 44, borderRadius: 8, border: 'none',
  background: '#059669', color: '#fff', cursor: 'pointer', fontWeight: 600,
};

export default PortalMemberDetail;
